import React from "react";
//#region types
import {
  DimensionData,
  DimensionSelectDialogProps,
  DimensionSelectDialogState,
} from "models/propTypes/dimensionPropTypes";
import { DialogEnums } from "models/enums";
import { TabDialogContentType } from "models/propTypes/dialogPropTypes";
import { SelectionKeyType, Preset, SelectionItem } from "models/utilTypes/selectionTypes";
import { TranslationKeys } from "translations";
import { IconDefinitionConstants } from "utils/constants";
//#endregion
//#region components
import { Grid } from "@mui/material";
import Icon from "../icon";
import ResultItemList from "../lists/resultItemList";
import DimensionInputsContainer from "./dimensionInputsContainer";
import FileViwerContextProvider from "../fileViewer/context/contextProvider";
import TabContentDialog from "../dialogs/_tabContentDialog";
import SubTitleWithIcon from "../titles/subTitleWithIcon";
import DimensionPresetSelector from "./dimensionPresetSelector";
import InputField from "../forms/baseFields/inputField";
import DimensionsDialogFilePortal from "./dimensionsDialogFilePortal";
//#endregion
import { LocalizationHelperInstance, MathHelper } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";
import dimensionHelper from "utils/helpers/dimensionHelper";

class DimensionSelectDialog extends React.Component<DimensionSelectDialogProps, DimensionSelectDialogState> {
  constructor(props: DimensionSelectDialogProps) {
    super(props);
    this.state = { ...this.getInitialState() };
  }

  getInitialState = (): DimensionSelectDialogState => ({
    searchText: "",
    searchResult: this.props.allDimensions,
    selectedItems: JSON.parse(JSON.stringify(this.props.selectedItems)),
    remainingAmount: this.props.amount,
    isValid: true,
    filePortalKey: this.state?.filePortalKey !== undefined ? this.state.filePortalKey + 1 : 0,
  });

  onSearchTabOpen = () => {
    this.setState(
      (prevState) => {
        return { ...prevState, searchText: "" };
      },
      () => {
        this.searchDimensions("");
      }
    );
  };

  onItemSelect = (item: SelectionItem) => {
    this.setState(
      (prevState) => {
        let isValid = false;
        let percentage: SelectionKeyType = this.props.amount === 0 ? 100 : 0;
        let amount: SelectionKeyType = 0;

        // Sign-aware: prefill the new row with what is left to allocate when the
        // remainder is non-zero, matches the total's sign, and is within its magnitude
        // (a negative total = refund/credit note leaves a negative remainder).
        const remaining = prevState.remainingAmount as number;
        const total = this.props.amount;
        if (
          total !== 0 &&
          remaining !== 0 &&
          Math.sign(remaining) === Math.sign(total) &&
          Math.abs(remaining) <= Math.abs(total)
        ) {
          percentage = MathHelper.toFixedNoRound((remaining / total) * 100, 2);
          amount = MathHelper.toFixedNoRound(remaining, 2);
          isValid = true;
        }

        const selectedItems = this.props.enableSingleSelectionMode
          ? [
              {
                ...item,
                data: {
                  dimensionId: item.id,
                  amount: this.props.amount,
                  percentage: percentage,
                  fraction: [1, 1],
                  isValid: true,
                } as DimensionData,
              },
            ]
          : [
              ...prevState.selectedItems,
              {
                ...item,
                data: {
                  dimensionId: item.id,
                  amount: amount,
                  percentage: percentage,
                  fraction: [amount, this.props.amount],
                  isValid: isValid,
                } as DimensionData,
              },
            ];

        return {
          ...prevState,
          selectedItems: selectedItems,
        };
      },
      () => {
        this.OnChangeSelectedItems();
      }
    );
  };

  onItemRemove = (id: SelectionKeyType) => {
    this.setState(
      (prevState) => {
        return {
          ...prevState,
          selectedItems: [...prevState.selectedItems.filter((x) => x.id !== id)],
        };
      },
      () => {
        this.OnChangeSelectedItems();
      }
    );
  };

  OnChangeSelectedItems = () => {
    this.setCalculatedValues();
  };

  onSearchTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const searchText = event.target.value;
    this.setState({ ...this.state, searchText: searchText }, () => {
      this.searchDimensions(searchText);
    });
  };

  searchDimensions = (searchText: string) => {
    const result = searchText
      ? this.props.allDimensions.filter((item) => {
          return item.value.toLowerCase().includes(searchText.toLowerCase());
        })
      : this.props.allDimensions;

    this.setState({ ...this.state, searchResult: result });
  };

  onDialogOpen = () => {
    this.setState(this.getInitialState(), () => {
      this.setCalculatedValues();
    });
  };

  onItemValuesUpdate = (id: string, amount: number, fraction: [number, number]) => {
    const selectedItems = [...this.state.selectedItems];
    const selectedItem = selectedItems.find((x) => x.id === id);
    if (selectedItem) {
      selectedItem.data = {
        dimensionId: id,
        amount: amount,
        percentage: MathHelper.toFixedNoRound((fraction[0] / fraction[1]) * 100, 2),
        fraction: fraction,
        isValid: true,
      };
    }
    this.setState(
      {
        ...this.state,
        selectedItems: selectedItems,
      },
      () => {
        this.OnChangeSelectedItems();
      }
    );
  };

  setCalculatedValues = () => {
    this.setState((prevState) => {
      let amount: number = 0;
      let isValid: boolean = true;
      prevState.selectedItems.forEach((item) => {
        const dimVal = item.data as DimensionData;
        amount = +amount + +dimVal.amount;
        // Sign-aware: a row is valid when it is non-zero and matches the total's sign
        // (negative total = refund/credit note); zero-total rows must be exactly 0.
        const rowOk =
          this.props.amount === 0
            ? +dimVal.amount === 0
            : this.props.amount > 0
              ? +dimVal.amount > 0
              : +dimVal.amount < 0;
        isValid = isValid ? rowOk : isValid;
      });

      ///due to precision issues in JS:
      ///https://stackoverflow.com/questions/1458633/how-to-deal-with-floating-point-number-precision-in-javascript
      const remaining = JSON.parse(MathHelper.toFixedNoRound(this.props.amount - amount, 2).toString());
      return {
        ...prevState,
        // eslint-disable-next-line eqeqeq --- since remaining can be of any type
        remainingAmount: remaining == 0 ? 0 : remaining,
        isValid: isValid,
      };
    });
  };

  onSave = () => {
    return new Promise<void>((resolve) => {
      if (!this.state.isValid) resolve();

      this.props.selectItemsHandler(this.state.selectedItems);
      this.props.cancelHandler();
      resolve();
    });
  };

  getFooterIcon = () => {
    let iconClass = cssExports.success;
    let icon = IconDefinitionConstants.WCheck;

    if (this.state.remainingAmount !== 0) {
      // Sign-aware: over-allocation makes the remainder overshoot zero (opposite sign
      // to the total) → error; still-to-allocate (same sign as the total) → warning.
      const overAllocated = this.props.amount < 0 ? this.state.remainingAmount > 0 : this.state.remainingAmount < 0;
      iconClass = overAllocated ? cssExports.error : cssExports.warning;
      icon = IconDefinitionConstants.WExclamation;
    }

    return <Icon icon={icon} className={iconClass} />;
  };

  //#region preset
  onPresetSelected = (selectedPreset: Preset) => {
    this.setState(
      (prevState) => ({
        ...prevState,
        selectedItems: this.convertPresetToSelectionItem(selectedPreset),
      }),
      () => {
        this.setCalculatedValues();
      }
    );
  };

  private convertPresetToSelectionItem = (preset: Preset) => {
    return preset.presetRows
      .filter((pr) => this.props.allDimensions.find((d) => d.id === pr.id))
      .map(
        (row): SelectionItem<DimensionData> => ({
          id: row.id,
          data: {
            dimensionId: row.id,
            amount: (this.props.amount * row.percentage) / 100,
            percentage: row.percentage,
            fraction: [row.percentage, 100],
            isValid: false,
          },
          value: this.props.allDimensions.find((d) => d.id === row.id)?.value ?? "",
        })
      );
  };

  //#endregion

  getUpdatedDimensionSuggestions = () => {
    return this.props.allDimensions.filter((dimension) => {
      return (
        this.props.dimensionSuggestions.find((s) => s.id === dimension.id) ||
        this.state.selectedItems.find((s) => s.id === dimension.id)
      );
    });
  };

  getDimensionsList = (items: SelectionItem[]) => {
    return (
      <ResultItemList
        items={items}
        onClickHandler={this.onItemSelect.bind(this)}
        selectedIds={this.state.selectedItems.map((item) => {
          return { id: item.id, data: item.data };
        })}
        canRemove={true}
        additionalContentOnSelection={
          this.props.enableSingleSelectionMode ? undefined : (
            <DimensionInputsContainer
              dimensionId={""}
              amount={0}
              // eslint-disable-next-line eqeqeq --- amount is received as a string as well
              percentage={this.props.amount == 0 ? 100 : 0}
              onChange={this.onItemValuesUpdate.bind(this)}
              total={this.props.amount}
              currency={this.props.currency}
            />
          )
        }
        additionalContentColumnCount={6}
        onRemoveHandler={this.onItemRemove.bind(this)}
      />
    );
  };

  render() {
    return (
      <FileViwerContextProvider>
        <TabContentDialog
          isOpen={this.props.isOpen}
          maxWidth="md"
          title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.SelectProperty, undefined, {
            property: dimensionHelper
              .getDimensionTypeText(this.props.dimensionCompatibilityType, this.props.dimensionTypeName)
              .toLowerCase(),
          })}
          titleIcon={IconDefinitionConstants.WHashtag}
          dialogType={DialogEnums.DialogType.TabContent}
          subTitle={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.IncomingInvoice.DialogSubTitle)}
          children={
            [
              {
                title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Suggestions),
                content: (
                  <>
                    <Grid container className={cssExports.asyncResultListContainer}>
                      <Grid
                        container
                        item
                        justifyContent="space-between"
                        alignItems="center"
                        className={cssExports.asyncResultListTop}
                      >
                        <Grid item>
                          <SubTitleWithIcon
                            subTitle={LocalizationHelperInstance.getFormattedMessage(
                              TranslationKeys.Common.SuggestionsMessage
                            )}
                            icon={IconDefinitionConstants.WBolt}
                          ></SubTitleWithIcon>
                        </Grid>
                        {this.props.enableSingleSelectionMode || this.props.dimensionPresetType === undefined ? (
                          <></>
                        ) : (
                          <Grid item>
                            <DimensionPresetSelector
                              dimensionPresetType={this.props.dimensionPresetType}
                              dimensionType={this.props.dimensionType}
                              selectedItems={this.state.selectedItems}
                              isValid={this.state.isValid}
                              onPresetSelected={this.onPresetSelected.bind(this)}
                            />
                          </Grid>
                        )}
                      </Grid>
                      {this.getDimensionsList(this.getUpdatedDimensionSuggestions())}
                    </Grid>
                  </>
                ),
              },
              {
                title: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.All),
                content: (
                  <>
                    <Grid container direction="column">
                      <InputField
                        inputBaseProps={{
                          placeholder: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Search),
                          value: `${this.state.searchText}`,
                          onChange: this.onSearchTextChange.bind(this),
                        }}
                        icon={IconDefinitionConstants.WSearch}
                        rootClass={cssExports.searchInput}
                      />
                      <Grid container className={cssExports.asyncResultListContainer}>
                        {this.getDimensionsList(this.state.searchResult)}
                      </Grid>
                    </Grid>
                  </>
                ),
                onTabChangeHandler: this.onSearchTabOpen.bind(this),
              },
            ] as TabDialogContentType
          }
          closeDialog={this.props.cancelHandler.bind(this)}
          onEnterHandler={this.onDialogOpen.bind(this)}
          customContent={
            this.props.fileViewer ? (
              <DimensionsDialogFilePortal key={this.state.filePortalKey}>
                {this.props.fileViewer}
              </DimensionsDialogFilePortal>
            ) : undefined
          }
          actions={[
            {
              text: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.Save),
              onClick: this.onSave.bind(this),
              disabled:
                (this.props.amount < 0 ? this.state.remainingAmount > 0 : this.state.remainingAmount < 0) ||
                !this.state.isValid,
            },
          ]}
          footer={
            this.props.hideFooter ? (
              <></>
            ) : (
              <Grid item className={cssExports.dimensionSelectorFooter}>
                <label>
                  {LocalizationHelperInstance.getFormattedMessage(
                    TranslationKeys.Common.DimensionSplitMessage,
                    undefined,
                    {
                      amount: LocalizationHelperInstance.getFormattedCurrency(
                        this.props.amount,
                        this.props.currency,
                        2,
                        2
                      ),
                    }
                  )}
                  :
                </label>
                <label className={cssExports.dimensionSelectFooterValue}>
                  {LocalizationHelperInstance.getFormattedCurrency(
                    this.state.remainingAmount,
                    this.props.currency,
                    2,
                    2
                  )}
                </label>
                <label className={cssExports.dimensionSelectFooterIcon}>{this.getFooterIcon()}</label>
              </Grid>
            )
          }
          fullWidth={true}
        />
      </FileViwerContextProvider>
    );
  }
}

export default DimensionSelectDialog;
